import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { ErrorState } from '../../components/LoadingSpinner';
import type { ResourceError } from './useAdminResource';
import { describeError } from './labels';
import { Icon, inputClass, primaryButton, secondaryButton } from '../../components/admin/AdminUI';

/** Label / value pairs in two columns. */
export function FactList({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 text-sm">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</dt>
          <dd className="mt-1 text-slate-900 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Search box that only asks the server when the admin submits. */
export function DirectorySearch({
  label,
  placeholder,
  value,
  onSearch,
}: {
  label: string;
  placeholder: string;
  value: string;
  onSearch: (q: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Follow the URL when it changes underneath us (Back, a cleared search).
  useEffect(() => setDraft(value), [value]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(draft.trim());
  };

  return (
    <form role="search" onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
      <div className="relative flex-1">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <Icon name="search" className="h-4 w-4" />
        </span>
        <input
          type="search"
          aria-label={label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          maxLength={200}
          className={`${inputClass} pl-9`}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          className={primaryButton}
        >
          Search
        </button>
        {value && (
          <button
            type="button"
            onClick={() => {
              setDraft('');
              onSearch('');
            }}
            className={secondaryButton}
          >
            Clear
          </button>
        )}
      </div>
    </form>
  );
}

/** The error state plus a way to try again. */
export function DirectoryError({
  error,
  what,
  onRetry,
}: {
  error: ResourceError;
  what: string;
  onRetry: () => void;
}) {
  return (
    <div className="space-y-3" data-testid="directory-error">
      <ErrorState message={describeError(error, what)} />
      {error.status !== 404 && error.status !== 400 && error.status !== 403 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onRetry}
            className={secondaryButton}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

/** 1-based page from the URL; anything else is page 1. */
export function pageFromParam(raw: string | null): number {
  if (!raw || !/^\d{1,6}$/.test(raw)) return 1;
  const n = parseInt(raw, 10);
  return n >= 1 ? n : 1;
}

/**
 * Where "back" goes: the list page (with its search and page) the admin came
 * from, when the router state says so; otherwise the bare list.
 */
export function backTarget(state: unknown, fallback: string): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : fallback;
}
